import type { ReactElement } from 'react';
import type { Availability } from '../../lib/api/queries/equipment.js';

export interface AvailabilitySegment {
  start: string;
  end: string;
  free: boolean;
}

/** Merges the two interval lists into one chronological sequence. */
export function toSegments(availability: Availability): AvailabilitySegment[] {
  return [
    ...availability.free.map((interval) => ({ ...interval, free: true })),
    ...availability.blocked.map((interval) => ({ ...interval, free: false })),
  ].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatRange(start: string, end: string): string {
  return `${formatDay(start)} — ${formatDay(end)}`;
}

export interface AvailabilityTimelineProps {
  segments: AvailabilitySegment[];
  /** The requested window, which sets the bar's scale. */
  from: string;
  to: string;
}

/**
 * The desktop presentation: one proportional bar across the requested window.
 *
 * It is `aria-hidden` and shown only from `lg`. This is not a degradation — the
 * bar conveys proportion, which is a visual property, and every interval it
 * draws is also present as text in `AvailabilitySummary`. A screen reader and a
 * phone therefore lose nothing, and the bar never becomes the only copy of a
 * fact.
 */
export function AvailabilityTimeline({
  segments,
  from,
  to,
}: AvailabilityTimelineProps): ReactElement {
  const windowStart = new Date(from).getTime();
  const windowEnd = new Date(to).getTime();
  const span = Math.max(1, windowEnd - windowStart);

  return (
    <div className="availability__timeline" aria-hidden="true">
      {segments.map((segment) => {
        const start = Math.max(windowStart, new Date(segment.start).getTime());
        const end = Math.min(windowEnd, new Date(segment.end).getTime());
        const width = Math.max(0, ((end - start) / span) * 100);
        if (width === 0) return null;
        return (
          <span
            key={`${segment.start}-${segment.free ? 'free' : 'blocked'}`}
            className={`availability__band availability__band--${
              segment.free ? 'free' : 'blocked'
            }`}
            style={{ width: `${width}%` }}
          />
        );
      })}
    </div>
  );
}
